import React from 'react';
import { Button, Input, Loader, Toggle, useToasts } from 'freemium-ui';
import { get } from 'lodash';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useUpdateAccountInfo } from 'shared/hooks';
import { FormHeader, FreshLink } from 'shared/styles';
import {
  AccountInfoType,
  ConfirmationType,
  TenantInfoType,
} from 'shared/types';
import { useGetTenantInfo, useSaveTenantInfo } from '../../hooks';
import { Translation } from './styles';
import GoogleTranslation from '../GoogleTranslation';

type TranslationProps = {
  setConfirmationData: (
    str: ConfirmationType | React.SetStateAction<ConfirmationType>,
  ) => void;
  accountInfo?: AccountInfoType;
};

const TranslationSettings = ({
  setConfirmationData,
  accountInfo,
}: TranslationProps) => {
  const { t } = useTranslation();
  const accountInfoForm = useForm<AccountInfoType>({});
  const tenantInfoForm = useForm<TenantInfoType>({});

  const [, setToast] = useToasts();

  const response_translation_enabled = accountInfoForm.watch(
    'account_meta.response_translation_enabled',
  );

  const is_question_translation_enabled = tenantInfoForm.watch(
    'account_meta.is_question_translation_enabled',
  );

  const google_translate_api_key = accountInfoForm.watch(
    'account_meta.google_translate_api_key',
  );

  const {
    data: tenantInfo,
    isLoading: isTenantLoading,
    isFetching: isFetchingTenantInfo,
  } = useGetTenantInfo();

  const { mutate: saveTenantInfo, isLoading: isLoadingTenantInfo } =
    useSaveTenantInfo({});

  const { mutate: updateAccount, isLoading: isLoadingAccountInfo } =
    useUpdateAccountInfo({
      callback: (success: boolean, data: AccountInfoType) => {
        if (success && data?.account_meta?.response_translation_enabled) {
          setToast({
            type: 'success',
            delay: 3000,
            text: `${t('forms.toast.success.translate_key')}`,
          });
        } else {
          accountInfoForm.setError('account_meta.google_translate_api_key', {
            message: `${t('forms.toast.error.Invalid_translate_key')}`,
          });
        }
      },
    });

  const onSubmit = (data) => {
    updateAccount(data);
  };

  const dismissConfirmation = () => {
    setConfirmationData({
      id: new Date().toISOString(),
      title: `${t('confirmation')}`,
      onConfirm: () => {
        accountInfoForm.setValue(
          'account_meta.response_translation_enabled',
          false,
        );

        updateAccount({
          ...accountInfo,
          account_meta: {
            ...accountInfo?.account_meta,
            response_translation_enabled: false,
            google_translate_api_key: '',
          },
        });
        setConfirmationData({});
      },
      onDismiss: () => {
        accountInfoForm.setValue(
          'account_meta.response_translation_enabled',
          true,
        );
      },
      confirmButtonText: `${t('confirm')}`,
      description: (
        <>
          <div>{t('forms.confirmation.response_translation_disabling')}</div>
          <div style={{ marginTop: '14px', fontWeight: 600 }}>
            {t('forms.confirmation.confirm_to_proceed')}
          </div>
        </>
      ),
    });
  };

  React.useEffect(() => {
    accountInfoForm.reset(accountInfo);
  }, [accountInfo]);

  React.useEffect(() => {
    tenantInfoForm.reset(tenantInfo);
  }, [tenantInfo]);

  return (
    <Translation>
      <FormHeader hideBorder>
        <div>
          <div className="heading">
            {t('settings.translation_settings.heading')}
          </div>
          <div className="sub-heading">
            {t('settings.translation_settings.description')}
          </div>
        </div>
      </FormHeader>
      <div className="translation">
        <div id="survey-question-container" className="translation-container">
          <div className="enable-translation">
            <div className="heading" id="ques-trans-label">
              {t('settings.translation_settings.survey_question.heading')}
            </div>
            <div className="description">
              {t('settings.translation_settings.survey_question.description')}
            </div>
          </div>
          <div>
            <Toggle
              {...{ 'aria-labelledby': 'ques-trans-label' }}
              disabled={
                isLoadingTenantInfo || isTenantLoading || isFetchingTenantInfo
              }
              hasIcon={false}
              on={is_question_translation_enabled === 'true'}
              handleChange={() => {
                saveTenantInfo({
                  payload: {
                    ...tenantInfo,
                    account_meta: {
                      ...tenantInfo?.account_meta,
                      is_question_translation_enabled:
                        is_question_translation_enabled === 'true'
                          ? 'false'
                          : 'true',
                    },
                  },
                });
              }}
            />
          </div>
        </div>
        <GoogleTranslation />
      </div>
      <div className="translation">
        <div id="responses-container" className="translation-container">
          <div className="enable-translation">
            <div className="heading" id="res-trans-label">
              {t('settings.translation_settings.responses.heading')}
            </div>
            <div className="description">
              {t('settings.translation_settings.responses.description')}
            </div>
          </div>
          <div>
            <Toggle
              {...{ 'aria-labelledby': 'res-trans-label' }}
              hasIcon={false}
              on={!!response_translation_enabled}
              handleChange={() => {
                if (response_translation_enabled) {
                  dismissConfirmation();
                } else {
                  accountInfoForm.setValue(
                    'account_meta.response_translation_enabled',
                    true,
                  );
                }
              }}
            />
          </div>
        </div>
        <GoogleTranslation />
        {response_translation_enabled && (
          <div className="translate-key-container">
            <div className="translate-key">
              <div>{t('forms.placeholders.google_translate_key')}</div>
              <FreshLink
                style={{ color: '#2c5cc5' }}
                href="https://support.freshsurvey.io/en/support/solutions/articles/50000008475-how-to-get-google-translate-api-key-"
                target="_blank"
              >
                {t('settings.translation_settings.get_translate_key')}
              </FreshLink>
            </div>
            <div className="translate-key-field">
              <Input
                {...accountInfoForm.register(
                  'account_meta.google_translate_api_key',
                )}
                placeholder={`${t('forms.placeholders.google_translate_key')}`}
                isErrored={
                  !!get(
                    accountInfoForm?.formState?.errors,
                    'account_meta.google_translate_api_key.message',
                  )
                }
                errorText={get(
                  accountInfoForm?.formState?.errors,
                  'account_meta.google_translate_api_key.message',
                  '',
                )}
              />
            </div>
            <Button
              disabled={!google_translate_api_key || isLoadingAccountInfo}
              inline
              className="save-key"
              onClick={(event: React.MouseEvent) => {
                event.preventDefault();
                accountInfoForm.handleSubmit(onSubmit, (err) =>
                  console.log('ERR', err),
                )();
              }}
            >
              {isLoadingAccountInfo ? <Loader size="small" /> : t('save')}
            </Button>
          </div>
        )}
      </div>
    </Translation>
  );
};

export default TranslationSettings;
